import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, CaretUp, CaretDown, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ICONS, ICON_NAMES, iconFor } from "@/lib/navIcons";

const WIDGET_TYPES = ["stat_card", "quick_link", "welcome_banner", "recent_list", "chart", "text"];
const STAT_SOURCES = ["customers", "leads", "orders", "invoices", "quotations", "payments", "products", "followups"];
const EMPTY = { type: "stat_card", title: "", subtitle: "", icon: "House", color: "", width: 1, height: 1, enabled: true, roles: [], config: {} };

function WidgetDialog({ open, onOpenChange, initial, onSave }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { setF(initial ? Object.assign({}, EMPTY, initial) : EMPTY); }, [initial, open]);
  const upd = (patch) => setF(Object.assign({}, f, patch));
  const cfg = (patch) => upd({ config: Object.assign({}, f.config || {}, patch) });
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    try { await onSave(f); onOpenChange(false); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Save failed"); }
  };
  const Ic = iconFor(f.icon);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="widget-dialog">
        <DialogHeader><DialogTitle>{initial && initial.id ? "Edit widget" : "New widget"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">TYPE</Label>
              <Select value={f.type} onValueChange={(v) => upd({ type: v })}>
                <SelectTrigger data-testid="widget-type"><SelectValue /></SelectTrigger>
                <SelectContent>{WIDGET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">TITLE</Label>
              <Input value={f.title} onChange={(e) => upd({ title: e.target.value })} data-testid="widget-title" />
            </div>
          </div>
          <div><Label className="text-xs">SUBTITLE</Label>
            <Input value={f.subtitle || ""} onChange={(e) => upd({ subtitle: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">WIDTH (1–4)</Label>
              <Input type="number" min={1} max={4} value={f.width} onChange={(e) => upd({ width: Math.min(4, Math.max(1, Number(e.target.value))) })} /></div>
            <div><Label className="text-xs">HEIGHT (1–3)</Label>
              <Input type="number" min={1} max={3} value={f.height} onChange={(e) => upd({ height: Math.min(3, Math.max(1, Number(e.target.value))) })} /></div>
          </div>
          <div>
            <Label className="text-xs mb-2 block">ICON — <Ic size={14} className="inline mx-1" weight="duotone" /> {f.icon}</Label>
            <div className="grid grid-cols-12 gap-1 max-h-32 overflow-y-auto p-2 border rounded-md bg-secondary/30">
              {ICON_NAMES.slice(0, 48).map((name) => {
                const I = ICONS[name];
                return (
                  <button key={name} type="button" onClick={() => upd({ icon: name })} title={name}
                    className={"w-7 h-7 rounded flex items-center justify-center " + (f.icon === name ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}>
                    <I size={14} weight="duotone" />
                  </button>
                );
              })}
            </div>
          </div>
          <div><Label className="text-xs">ACCENT COLOR (hex, optional)</Label>
            <Input value={f.color || ""} onChange={(e) => upd({ color: e.target.value })} placeholder="#4a2f1e" /></div>

          {/* Per-type config */}
          {f.type === "stat_card" && (
            <div>
              <Label className="text-xs">SOURCE (count of…)</Label>
              <Select value={(f.config && f.config.source) || ""} onValueChange={(v) => cfg({ source: v })}>
                <SelectTrigger data-testid="widget-source"><SelectValue placeholder="Pick a source" /></SelectTrigger>
                <SelectContent>{STAT_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.type === "quick_link" && (
            <div><Label className="text-xs">PATH</Label>
              <Input className="font-mono" value={(f.config && f.config.path) || ""} onChange={(e) => cfg({ path: e.target.value })} placeholder="/leads" /></div>
          )}
          {f.type === "welcome_banner" && (
            <div><Label className="text-xs">MESSAGE</Label>
              <Textarea rows={3} value={(f.config && f.config.message) || ""} onChange={(e) => cfg({ message: e.target.value })} placeholder="Good morning, {name} 👋" /></div>
          )}
          {f.type === "recent_list" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">COLLECTION</Label>
                <Select value={(f.config && f.config.source) || ""} onValueChange={(v) => cfg({ source: v })}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{STAT_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">LIMIT</Label>
                <Input type="number" min={3} max={20} value={(f.config && f.config.limit) || 5}
                  onChange={(e) => cfg({ limit: Number(e.target.value) })} /></div>
            </div>
          )}
          {f.type === "chart" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">METRIC</Label>
                <Select value={(f.config && f.config.metric) || ""} onValueChange={(v) => cfg({ metric: v })}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{STAT_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">STYLE</Label>
                <Select value={(f.config && f.config.style) || "line"} onValueChange={(v) => cfg({ style: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["line", "bar", "area"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          {f.type === "text" && (
            <div><Label className="text-xs">CONTENT (markdown supported)</Label>
              <Textarea rows={4} value={(f.config && f.config.content) || ""} onChange={(e) => cfg({ content: e.target.value })} /></div>
          )}

          <label className="flex items-center gap-2 text-xs">
            <Switch checked={f.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="widget-enabled" />
            Enabled
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="widget-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UIBuilder() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/platform/widgets"); setWidgets(r.data); }
    catch { toast.error("Failed to load widgets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (editing && editing.id) {
      await api.put("/platform/widgets/" + editing.id, payload);
      toast.success("Widget updated");
    } else {
      await api.post("/platform/widgets", payload);
      toast.success("Widget added");
    }
    load();
  };
  const remove = async (w) => {
    if (w.is_system) return toast.error("System widget — disable it instead");
    if (!window.confirm("Delete “" + w.title + "”?")) return;
    try { await api.delete("/platform/widgets/" + w.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Delete failed"); }
  };
  const toggle = async (w) => { await api.put("/platform/widgets/" + w.id, { enabled: !w.enabled }); load(); };
  const move = async (w, dir) => {
    const idx = widgets.findIndex((x) => x.id === w.id);
    const j = idx + dir;
    if (j < 0 || j >= widgets.length) return;
    const next = widgets.slice();
    const tmp = next[idx]; next[idx] = next[j]; next[j] = tmp;
    const items = next.map((x, i) => ({ id: x.id, order: i * 10 }));
    await api.post("/platform/widgets/reorder", { items });
    load();
  };
  const resetAll = async () => {
    if (!window.confirm("Reset dashboard widgets to system defaults?")) return;
    await api.post("/platform/widgets/reset");
    toast.success("Reset to defaults"); load();
  };

  return (
    <Card className="p-6" data-testid="ui-builder">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline text-muted-foreground">Dashboard</div>
          <h3 className="font-heading font-bold text-xl">UI Builder</h3>
          <p className="text-xs text-muted-foreground mt-1">Compose the tenant dashboard: pick widget type, wire its data source, reorder with ↑/↓, control visibility. Branding (logo/colors/announcements) lives in the Branding tab.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetAll} data-testid="widget-reset"><ArrowsClockwise size={14} className="mr-1.5" />Reset</Button>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="widget-add"><Plus size={14} className="mr-1.5" />New widget</Button>
        </div>
      </div>
      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="space-y-2">
          {widgets.map((w, i) => {
            const Ic = iconFor(w.icon);
            return (
              <div key={w.id} className={"border rounded-md p-3 flex items-center gap-3 " + (w.enabled ? "" : "opacity-50")}
                data-testid={"widget-row-" + w.id}>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => move(w, -1)} disabled={i === 0} className="disabled:opacity-30 text-muted-foreground p-0.5"><CaretUp size={12} /></button>
                  <button onClick={() => move(w, 1)} disabled={i === widgets.length - 1} className="disabled:opacity-30 text-muted-foreground p-0.5"><CaretDown size={12} /></button>
                </div>
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0" style={w.color ? { background: w.color + "22", color: w.color } : {}}>
                  <Ic size={15} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {w.title}
                    <span className="text-[10px] uppercase text-primary/70 border border-primary/30 px-1 rounded font-mono">{w.type}</span>
                    {w.is_system && <span className="text-[9px] uppercase text-primary/70 border border-primary/30 px-1 rounded">system</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {w.width}×{w.height} · {w.subtitle || <span className="italic">no subtitle</span>}
                    {w.config?.source && <> · source: <span className="font-mono">{w.config.source}</span></>}
                    {w.config?.path && <> · → <span className="font-mono">{w.config.path}</span></>}
                  </div>
                </div>
                <Switch checked={w.enabled} onCheckedChange={() => toggle(w)} data-testid={"widget-toggle-" + w.id} />
                <button onClick={() => { setEditing(w); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit" data-testid={"widget-edit-" + w.id}>
                  <PencilSimple size={13} weight="duotone" />
                </button>
                {!w.is_system && (
                  <button onClick={() => remove(w)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={"widget-delete-" + w.id}>
                    <Trash size={13} weight="duotone" />
                  </button>
                )}
              </div>
            );
          })}
          {!widgets.length && <div className="py-12 text-center text-muted-foreground text-sm">No widgets yet.</div>}
        </div>
      )}
      <WidgetDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSave={save} />
    </Card>
  );
}
